/**
 * Auth_Service.ts
 * API functions for Platform auth endpoints.
 *
 * Cookies (accessToken + refreshToken) are httpOnly — never touched here.
 * Only non-sensitive UI data (orgId) is stored via Token_Handler.
 */

import axiosInstance from '../../api-endpoints/axiosInstance';
import { Token_Handler } from '../../storage/tokenHandler';
import { restoreHandoff } from '../../navigation/productHandoff';
import {
  Platform_Signup_Url,
  Platform_Me_Url,
  Platform_Logout_Url,
  Platform_Forgot_Password_Url,
  Platform_Reset_Password_Url,
  Platform_Reset_Password_Claim_Url,
  Platform_Verify_Email_Url,
  Platform_Resend_Verification_Url,
  Platform_Switch_Org_Url,
  Platform_Login_Mfa_Url,
  Platform_Accept_Invitation_Url,
  Platform_Invites_Decline_Url,
  Platform_Invite_Preview_Url,
  Platform_Prepaid_Order_Url,
} from '../../api-endpoints/api-endpoints';
import {
  SignupPayload,
  SignupResponse,
  CompleteProfilePayload,
  CompleteProfileResponse,
  PrepaidOrderView,
} from '../../schema/auth/signup';
import {
  AcceptInvitationPayload,
  AcceptInvitationResponse,
  InvitationPreviewResponse,
} from '../../schema/auth/invitation';

// The product the user arrived from (from the persisted handoff) — drives which
// product app the recovery / verification email links back to. NEVER 'platform'
// (not a real product slug; the API rejects it with invalid_enum_value). When
// there's no originating product we OMIT it and let the API apply its default.
function originatingProduct(): string | undefined {
  return restoreHandoff()?.product || undefined;
}

/* ─────────────────────────────────────────
   Signup — POST /v1/auth/signup  [anonymous]

   ONE request creates the whole account: credentials + name + company +
   mobile + business region. The server writes the profile and stamps
   onboarding complete in the same transaction, so nothing is left to collect
   afterwards and there is no window where a half-built account exists.

   The verification email is still sent and the org is still born `inactive` —
   the response carries `verification_required: true`, and the user lands on
   the check-email screen exactly as before. Only the second FORM is gone.

   The profile block is all-or-nothing server-side: send every field or none.
   `SignupPayload` requires them all, so a partial payload is a type error
   here rather than a 400 at runtime.

   Centralized auth: when the user arrived from a product CTA we scope the
   signup to that product slug (e.g. 'catalog'), which drives both the new
   account's entitlement and the verification-email landing. With no originating
   product we OMIT `product` and let the API apply its own default.
───────────────────────────────────────── */

export const signupOrg = async (
  payload: SignupPayload,
  product?: string,
): Promise<SignupResponse> => {
  const { data } = await axiosInstance.post<SignupResponse>(Platform_Signup_Url, {
    ...payload,
    ...(product ? { product } : {}),
  });

  // Fresh account → start clean: clear any product namespaces left behind by a
  // previous account/session before persisting this signup's org id. Prevents
  // stale sk_<product>_* display data from surfacing after signup.
  await Token_Handler.clearAll();

  // The real org + user names are known now (they came in the same request),
  // so the shell chrome can be seeded immediately rather than waiting for a
  // second call that no longer happens.
  //
  // Guarded on truthiness, not just presence: the API deliberately returns a
  // blank-name `org_pending` / `usr_pending` envelope when the email already
  // belongs to a VERIFIED account (anti-enumeration — it looks identical to a
  // fresh signup). Persisting those blanks would wipe good display data, so
  // empty strings are skipped.
  await Promise.all([
    data?.data?.org?.id           && Token_Handler.setOrgId(data.data.org.id),
    data?.data?.org?.name         && Token_Handler.setBusinessName(data.data.org.name),
    data?.data?.user?.first_name  && Token_Handler.setFirstName(data.data.user.first_name),
    data?.data?.user?.last_name   && Token_Handler.setLastName(data.data.user.last_name),
  ].filter(Boolean));

  return data;
};

/* ─────────────────────────────────────────
   Pay before signup — GET /v1/public/checkout/orders/:id  [anonymous]

   The plan the visitor paid for on the website, read back with the claim
   secret Cashfree's return URL carried. Shown above the signup form and used
   to prefill it; the signup request then claims it. 404 = unknown order or a
   wrong secret — the caller drops the claim and shows a plain signup.
───────────────────────────────────────── */

export const fetchPrepaidOrder = async (orderId: string, token: string): Promise<PrepaidOrderView> => {
  const { data } = await axiosInstance.get<{ data: PrepaidOrderView }>(
    `${Platform_Prepaid_Order_Url}/${encodeURIComponent(orderId)}`,
    { params: { token } },
  );
  return data.data;
};

/* ─────────────────────────────────────────
   Complete profile — POST /v1/auth/signup  [authenticated]  · LEGACY PATH

   Same endpoint; the server detects the session plus the absence of
   credentials and treats the call as profile completion. Sends
   name/company/phone, runs the phone-uniqueness checks, and stamps onboarding
   complete.

   The single-flow signup never reaches this — its accounts are already
   complete, and calling it again returns 409 PROFILE_ALREADY_COMPLETE by
   design (the phone is a cross-product identity key and must not be
   rewritable after the fact). It remains for accounts created BEFORE the
   single flow, which still have a blank profile and are routed to the "Tell us
   about you" card on login.
───────────────────────────────────────── */

export const completeProfile = async (
  payload: CompleteProfilePayload,
): Promise<CompleteProfileResponse> => {
  const { data } = await axiosInstance.post<CompleteProfileResponse>(Platform_Signup_Url, payload);

  // Now we have the real identity — persist it for the shell chrome.
  await Promise.all([
    data?.data?.org?.id          && Token_Handler.setOrgId(data.data.org.id),
    data?.data?.org?.name        && Token_Handler.setBusinessName(data.data.org.name),
    data?.data?.user?.first_name && Token_Handler.setFirstName(data.data.user.first_name),
    data?.data?.user?.last_name  && Token_Handler.setLastName(data.data.user.last_name),
  ].filter(Boolean));

  invalidateMeCache();
  return data;
};

/* ─────────────────────────────────────────
   Me — GET /v1/me
   Module-level cache (5s TTL) + in-flight deduplicator so parallel callers
   (AuthContext, App_Navigator) share one request.
───────────────────────────────────────── */

const ME_TTL = 5_000;
let _meCache: { data: any; at: number } | null = null;
let _meInflight: Promise<any> | null = null;

export const invalidateMeCache = () => { _meCache = null; };

export const getCurrentUser = async (): Promise<any> => {
  if (_meCache && Date.now() - _meCache.at < ME_TTL) return _meCache.data;
  if (!_meInflight) {
    _meInflight = axiosInstance
      .get(Platform_Me_Url)
      .then(r => { _meCache = { data: r.data, at: Date.now() }; return r.data; })
      .finally(() => { _meInflight = null; });
  }
  return _meInflight;
};

/* ─────────────────────────────────────────
   Logout — POST /v1/auth/logout
───────────────────────────────────────── */

export const logoutUser = async (): Promise<void> => {
  try {
    await axiosInstance.post(Platform_Logout_Url);
  } finally {
    invalidateMeCache();
    await Token_Handler.clearAll();
  }
};

/* ─────────────────────────────────────────
   Forgot Password — POST /v1/auth/forgot-password
───────────────────────────────────────── */

export interface ForgotPasswordResponse {
  message: string;
}

export const forgotPassword = async (email: string): Promise<ForgotPasswordResponse> => {
  // Platform returns 204 (no body) on success to prevent enumeration —
  // axios then leaves `data` as an empty string. Synthesize a friendly
  // response so screens can always read `res.message` without a crash.
  //
  // If the user arrived from a product CTA, the reset link should return them to
  // THAT product's app. With no handoff, the user is on the SK Platform's own
  // auth screen, so we target 'platform' — the email then lands on this same FE
  // (skplatform-fe /?mode=reset), not on voice.* (the API's former default).
  const product = originatingProduct() ?? 'platform';
  const { data } = await axiosInstance.post<ForgotPasswordResponse | ''>(
    Platform_Forgot_Password_Url,
    { email, product },
  );
  return data && typeof data === 'object' && 'message' in data
    ? (data as ForgotPasswordResponse)
    : { message: 'If an account exists for that email, a reset link has been sent.' };
};

/* ─────────────────────────────────────────
   Reset Password — POST /v1/auth/reset-password
───────────────────────────────────────── */

export interface ResetPasswordResponse {
  message: string;
}

export const resetPassword = async (token: string, newPassword: string): Promise<ResetPasswordResponse> => {
  // API expects snake_case: new_password (not newPassword).
  // Platform returns 204 on success — handle the empty body the same way
  // forgotPassword does so the caller can always destructure a message.
  const { data } = await axiosInstance.post<ResetPasswordResponse | ''>(
    Platform_Reset_Password_Url,
    { token, new_password: newPassword },
  );
  return data && typeof data === 'object' && 'message' in data
    ? (data as ResetPasswordResponse)
    : { message: 'Your password has been reset. Please sign in.' };
};

/**
 * Claim an emailed reset link on page load (single-open). The server consumes
 * the link token and returns a fresh `grant` the form submits with — so the
 * link works exactly once, and re-opening it returns { ok:false } → the screen
 * shows an "invalid link" state. The grant never touches the URL, so it can't
 * be pre-fetched by email scanners. Returns null on { ok:false }; throws on a
 * transient network error so the caller can distinguish "dead link" from
 * "couldn't reach the server" and let the user retry.
 */
export const claimResetToken = async (token: string): Promise<string | null> => {
  if (!token) return null;
  const { data } = await axiosInstance.post<{ data?: { ok?: boolean; grant?: string } }>(
    Platform_Reset_Password_Claim_Url,
    { token },
  );
  const payload = (data as any)?.data ?? data;
  return payload?.ok && payload?.grant ? (payload.grant as string) : null;
};

/* ─────────────────────────────────────────
   Verify Email — POST /v1/auth/verify-email
   Token arrives via the emailed link; flips the org ACTIVE.
───────────────────────────────────────── */

export interface VerifyEmailResponse {
  data: {
    verified: boolean;
    user_id: string;
    activated_org_ids: string[];
    // Verify now auto-logs-in and returns onboarding state so the screen can
    // route the user straight to Step 2 (profile) when it's still pending.
    profile_complete?: boolean;
    user?: { id: string; email: string; first_name: string; last_name: string };
    org?: { id: string; name: string; status?: string } | null;
  };
}

export const verifyEmail = async (token: string): Promise<VerifyEmailResponse> => {
  const { data } = await axiosInstance.post<VerifyEmailResponse>(Platform_Verify_Email_Url, { token });
  // Verify auto-logs-in (the API sets the session cookies). Persist the org id
  // for the shell chrome; name/company arrive at Step 2.
  if (data?.data?.org?.id) await Token_Handler.setOrgId(data.data.org.id);
  invalidateMeCache();
  return data;
};

/* ─────────────────────────────────────────
   Resend Verification — POST /v1/auth/resend-verification
   AUTHENTICATED: the platform resends to the *logged-in session's own user*
   (no email argument — no enumeration surface). The caller must already hold
   a valid session cookie, which an unverified login does establish. Scoped to
   `product: 'catalog'` so the emailed link returns to this SPA (?mode=verify).
   Already-verified users are no-op'd server-side with a 204.
───────────────────────────────────────── */

export interface ResendVerificationResponse {
  message: string;
  // Server-enforced cooldown window (seconds) before another resend is allowed.
  // The screen drives its countdown from this so the FE always mirrors the
  // backend rate limit rather than guessing.
  retryAfterSeconds: number;
}

// Fallback window if the server response omits it (older API). Matches the
// backend's RESEND_VERIFY_WINDOW_SEC (3 minutes).
const RESEND_FALLBACK_COOLDOWN_SEC = 180;

export const resendVerification = async (): Promise<ResendVerificationResponse> => {
  // Platform returns 200 `{ message, retry_after_seconds }` on success. Older
  // builds returned an empty 204 — tolerate both so the screen can always read
  // `res.message` and `res.retryAfterSeconds`.
  const product = originatingProduct();
  const { data } = await axiosInstance.post<{ message?: string; retry_after_seconds?: number } | ''>(
    Platform_Resend_Verification_Url,
    product ? { product } : {},
  );
  const body = data && typeof data === 'object' ? data : {};
  return {
    message: body.message ?? 'Verification email sent. Please check your inbox.',
    retryAfterSeconds:
      typeof body.retry_after_seconds === 'number' && body.retry_after_seconds > 0
        ? body.retry_after_seconds
        : RESEND_FALLBACK_COOLDOWN_SEC,
  };
};

/* ─────────────────────────────────────────
   Complete MFA challenge — POST /v1/auth/login/mfa
   Called after login returns { kind: 'mfa_required', mfa_token }.
───────────────────────────────────────── */

export const completeMfa = async (mfaToken: string, code: string): Promise<any> => {
  const { data } = await axiosInstance.post(Platform_Login_Mfa_Url, { mfa_token: mfaToken, code });
  invalidateMeCache();
  return data;
};

/* ─────────────────────────────────────────
   Switch active org — POST /v1/auth/switch-org
   Mints fresh cookies scoped to another org the user belongs to.
───────────────────────────────────────── */

/* ─────────────────────────────────────────
   Accept / decline invitation
   POST /v1/auth/accept-invitation · POST /v1/invites/decline
───────────────────────────────────────── */

export const acceptInvitation = async (
  payload: AcceptInvitationPayload,
): Promise<AcceptInvitationResponse> => {
  const { data } = await axiosInstance.post<AcceptInvitationResponse>(
    Platform_Accept_Invitation_Url,
    payload,
  );
  invalidateMeCache();
  return data;
};

export const declineInvitation = async (token: string): Promise<void> => {
  await axiosInstance.post(Platform_Invites_Decline_Url, { token });
};

/**
 * Public, token-scoped preview — read-only, no auth required. Lets the
 * accept-invitation screen render the right state (form / merge / expired /
 * accepted / cancelled) and show the org name read-only BEFORE the invitee
 * submits anything, instead of guessing and finding out from a failed submit.
 */
export const getInvitationPreview = async (
  token: string,
): Promise<InvitationPreviewResponse> => {
  const { data } = await axiosInstance.get<InvitationPreviewResponse>(
    Platform_Invite_Preview_Url(token),
  );
  return data;
};

export const switchOrg = async (orgId: string): Promise<any> => {
  const { data } = await axiosInstance.post(Platform_Switch_Org_Url, { org_id: orgId });
  invalidateMeCache();
  if (data?.org?.id) await Token_Handler.setOrgId(data.org.id);
  if (data?.data?.org?.id) await Token_Handler.setOrgId(data.data.org.id);
  return data;
};
